import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "../lib/api";

/*
 * Viewport telemetry.
 *
 * The console is developed on a Windows box with no iPhone and no Simulator,
 * and Chromium is not WebKit — so every iOS layout change so far was reasoned
 * about from screenshots and shipped without landing. This page ends the
 * guessing: it reads what iOS actually reports and posts it to the server so
 * it can be read back off the host.
 *
 * env() insets cannot be read from JS directly, so a probe element takes them
 * as padding and we read the resolved pixels back off getComputedStyle. Same
 * trick for the viewport units, which is the only way to see whether dvh/svh/
 * lvh actually differ on this device.
 *
 * The keyboard bench below is the part that matters. The readout is unusable
 * while the keyboard is up — the keyboard is covering it — so tapping the test
 * box captures a snapshot on a timer, posts it, and renders it once the
 * keyboard closes again. That is the only way to see the keyboard-open numbers
 * from a machine that cannot run iOS.
 */

type Row = { k: string; v: string };

function measure(probe: HTMLElement, units: HTMLElement): Row[] {
  const vv = window.visualViewport;
  const cs = getComputedStyle(probe);
  const us = getComputedStyle(units);
  const rs = getComputedStyle(document.documentElement);
  const px = (s: string) => `${Math.round(parseFloat(s) || 0)}px`;

  const mode = ["standalone", "fullscreen", "minimal-ui", "browser"].find((m) =>
    window.matchMedia(`(display-mode: ${m})`).matches,
  );

  const shell = document.querySelector(".shell");
  const shellBox = shell ? shell.getBoundingClientRect() : null;

  return [
    { k: "display-mode", v: mode ?? "unknown" },
    // iOS-only legacy flag; true inside a home-screen web app.
    { k: "navigator.standalone", v: String((navigator as { standalone?: boolean }).standalone) },
    { k: "screen", v: `${screen.width} x ${screen.height}` },
    { k: "devicePixelRatio", v: String(window.devicePixelRatio) },
    { k: "window.inner", v: `${window.innerWidth} x ${window.innerHeight}` },
    { k: "window.outerHeight", v: String(window.outerHeight) },
    { k: "documentElement.clientHeight", v: String(document.documentElement.clientHeight) },
    { k: "visualViewport h", v: vv ? String(Math.round(vv.height)) : "n/a" },
    { k: "visualViewport offsetTop", v: vv ? String(Math.round(vv.offsetTop)) : "n/a" },
    { k: "visualViewport pageTop", v: vv ? String(Math.round(vv.pageTop)) : "n/a" },
    { k: "visualViewport scale", v: vv ? String(vv.scale) : "n/a" },
    // The four insets. All zero => viewport-fit=cover is not in effect.
    { k: "env safe-top", v: px(cs.paddingTop) },
    { k: "env safe-bottom", v: px(cs.paddingBottom) },
    { k: "env safe-left", v: px(cs.paddingLeft) },
    { k: "env safe-right", v: px(cs.paddingRight) },
    { k: "100dvh", v: px(us.height) },
    { k: "100svh", v: px(us.minHeight) },
    { k: "100lvh", v: px(us.maxHeight) },
    // What the hook is actually writing, and what the shell actually became.
    { k: "--kb (live)", v: rs.getPropertyValue("--kb").trim() || "unset" },
    { k: "--vv-top (live)", v: rs.getPropertyValue("--vv-top").trim() || "unset" },
    { k: "kb-open class", v: String(document.documentElement.classList.contains("kb-open")) },
    { k: ".shell top", v: shellBox ? String(Math.round(shellBox.top)) : "n/a" },
    { k: ".shell height", v: shellBox ? String(Math.round(shellBox.height)) : "n/a" },
    { k: ".shell bottom", v: shellBox ? String(Math.round(shellBox.bottom)) : "n/a" },
    { k: "scrollY", v: String(Math.round(window.scrollY)) },
    { k: "body scrollHeight", v: String(document.body.scrollHeight) },
    { k: "ua", v: navigator.userAgent },
  ];
}

export function DebugView() {
  const probe = useRef<HTMLDivElement>(null);
  const units = useRef<HTMLDivElement>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [kbRows, setKbRows] = useState<Row[]>([]);
  const [sent, setSent] = useState<string>("");
  const [hud, setHud] = useState({
    kb: "0px",
    vvTop: "0px",
    vvh: "n/a",
    inner: "n/a",
    kbOpen: false,
  });

  const read = useCallback(() => {
    if (!probe.current || !units.current) return;
    setRows(measure(probe.current, units.current));
  }, []);

  const post = useCallback(async (label: string, data: Row[]) => {
    try {
      const body = { when: label, ...Object.fromEntries(data.map((r) => [r.k, r.v])) };
      const res = await apiFetch("/api/debug/viewport", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const raw = await res.text();
      let detail = raw.slice(0, 240);
      try {
        const j = JSON.parse(raw) as { error?: string; ok?: boolean };
        if (j?.error) detail = j.error;
      } catch {
        /* keep the raw body */
      }
      setSent(
        res.ok
          ? `Sent (${label}). Tell the agent.`
          : `Failed: ${res.status} ${res.statusText} — ${detail || "(empty body)"}`,
      );
    } catch (e) {
      setSent(`Failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  }, []);

  useEffect(() => {
    read();
    // Safari's toolbar is still settling on first paint, so sample again.
    const t = [150, 600, 1500].map((ms) => window.setTimeout(read, ms));
    const vv = window.visualViewport;
    vv?.addEventListener("resize", read);
    vv?.addEventListener("scroll", read);
    window.addEventListener("resize", read);
    window.addEventListener("orientationchange", read);
    return () => {
      t.forEach(clearTimeout);
      vv?.removeEventListener("resize", read);
      vv?.removeEventListener("scroll", read);
      window.removeEventListener("resize", read);
      window.removeEventListener("orientationchange", read);
    };
  }, [read]);

  /* rAF so the HUD stays current *while the keyboard is open* — visualViewport
     events can miss frames of the show animation, and the table below is
     covered by the keyboard. */
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const rs = getComputedStyle(document.documentElement);
      const vv = window.visualViewport;
      const next = {
        kb: rs.getPropertyValue("--kb").trim() || "0px",
        vvTop: rs.getPropertyValue("--vv-top").trim() || "0px",
        vvh: vv ? String(Math.round(vv.height)) : "n/a",
        inner: String(window.innerHeight),
        kbOpen: document.documentElement.classList.contains("kb-open"),
      };
      setHud((prev) =>
        prev.kb === next.kb &&
        prev.vvTop === next.vvTop &&
        prev.vvh === next.vvh &&
        prev.inner === next.inner &&
        prev.kbOpen === next.kbOpen
          ? prev
          : next,
      );
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  /* Tapping the test box: let the keyboard finish animating, then freeze a
     snapshot and ship it. 700ms clears iOS's show animation comfortably. */
  const onFocus = () => {
    setSent("keyboard opening…");
    window.setTimeout(() => {
      if (!probe.current || !units.current) return;
      const snap = measure(probe.current, units.current);
      setKbRows(snap);
      void post("keyboard-open", snap);
    }, 700);
  };

  const table = (data: Row[]) => (
    <div style={{ fontFamily: "var(--mono)", fontSize: 12 }}>
      {data.map((r) => (
        <div
          key={r.k}
          style={{
            display: "flex",
            gap: 8,
            padding: "5px 0",
            borderBottom: "1px solid var(--line)",
            wordBreak: "break-all",
          }}
        >
          <span style={{ color: "var(--ink-mute)", flex: "0 0 46%" }}>{r.k}</span>
          <span style={{ flex: 1, textAlign: "right" }}>{r.v}</span>
        </div>
      ))}
    </div>
  );

  return (
    /*
     * Own scroll container. The shell is a locked 100svh column with
     * `.main { overflow: hidden }`, and this view has no `.page-body` to
     * inherit scrolling from — without this the readout clips on a phone and
     * the document cannot scroll to reach the rest. Functional, not a design
     * pass: this page stays deliberately plain.
     */
    <div
      style={{
        padding: 16,
        height: "100%",
        overflowY: "auto",
        overscrollBehavior: "contain",
        WebkitOverflowScrolling: "touch",
      }}
    >
      {/*
        The plain "send what is on screen" action, at the top where it can be
        found. The other send button lives inside the keyboard bench and is
        labelled for that experiment — which meant the only obvious control on
        the page opened the keyboard, and a normal readout took a detour
        through it.
      */}
      <button
        className="btn btn--primary"
        onClick={() => void post("readout", rows)}
        style={{ width: "100%", marginBottom: 12 }}
      >
        Send this readout to the host
      </button>
      {sent && (
        <div
          style={{
            fontSize: 12,
            marginBottom: 12,
            color: sent.startsWith("Failed") ? "var(--fault)" : "var(--ok)",
          }}
        >
          {sent}
        </div>
      )}
      {/* Probe: env() insets resolved into readable pixel padding. */}
      <div
        ref={probe}
        aria-hidden="true"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: 1,
          height: 1,
          visibility: "hidden",
          pointerEvents: "none",
          paddingTop: "env(safe-area-inset-top, 0px)",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
          paddingLeft: "env(safe-area-inset-left, 0px)",
          paddingRight: "env(safe-area-inset-right, 0px)",
        }}
      />
      {/* Probe: the three viewport units, smuggled through three properties. */}
      <div
        ref={units}
        aria-hidden="true"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: 1,
          visibility: "hidden",
          pointerEvents: "none",
          height: "100dvh",
          minHeight: "100svh",
          maxHeight: "100lvh",
        }}
      />

      <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Viewport report</h1>

      {/* Sticky HUD: the only numbers that can be read on a phone *while* the
          keyboard is covering the rest of the page. */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          margin: "12px 0",
          padding: 12,
          borderRadius: 10,
          border: "1px solid var(--line-strong)",
          background: "var(--surface-1)",
          fontFamily: "var(--mono)",
          fontSize: 13,
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 6, fontFamily: "var(--font)" }}>
          Keyboard (live)
        </div>
        {[
          ["--kb", hud.kb],
          ["--vv-top", hud.vvTop],
          ["visualViewport.height", hud.vvh],
          ["innerHeight", hud.inner],
          ["kb-open", hud.kbOpen ? "yes" : "no"],
        ].map(([k, v]) => (
          <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "2px 0" }}>
            <span style={{ color: "var(--ink-mute)" }}>{k}</span>
            <span>{v}</span>
          </div>
        ))}
      </div>

      {/* --- keyboard bench, kept at the very top so it stays reachable --- */}
      <div
        style={{
          border: "1px solid var(--line-strong)",
          borderRadius: 10,
          padding: 12,
          margin: "12px 0",
          background: "var(--surface-2)",
        }}
      >
        <p style={{ fontSize: 13, color: "var(--ink-soft)", marginBottom: 8 }}>
          <strong>Keyboard test.</strong> Tap the box, type one letter, then close the
          keyboard. The numbers are captured while the keyboard is up and sent
          automatically, so nothing has to be read through the keyboard.
        </p>
        <input
          type="text"
          onFocus={onFocus}
          placeholder="Tap here to open the keyboard"
          style={{
            width: "100%",
            minHeight: 44,
            padding: "0 12px",
            borderRadius: 8,
            border: "1px solid var(--line-strong)",
            background: "var(--surface-1)",
            /* 16px or iOS zooms the page on focus. */
            fontSize: 16,
          }}
        />
        {sent && (
          <div
            style={{
              fontSize: 13,
              color: sent.startsWith("Failed") ? "var(--fault)" : "var(--ok)",
              marginTop: 8,
              overflowWrap: "anywhere",
            }}
          >
            {sent}
          </div>
        )}
        <button
          className="btn btn--primary"
          onClick={() => void post("keyboard-closed", rows)}
          style={{ marginTop: 10 }}
        >
          Send closed-keyboard numbers
        </button>
      </div>

      {kbRows.length > 0 && (
        <>
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: "18px 0 2px" }}>
            Captured with keyboard OPEN
          </h2>
          <p style={{ fontSize: 12, color: "var(--ink-mute)", marginBottom: 8 }}>
            Frozen at capture time. Screenshot this if the send failed.
          </p>
          {table(kbRows)}
        </>
      )}

      <h2 style={{ fontSize: 15, fontWeight: 700, margin: "18px 0 2px" }}>Live now</h2>
      <p style={{ fontSize: 12, color: "var(--ink-mute)", marginBottom: 8 }}>
        Updates as the toolbar moves.
      </p>
      {table(rows)}
    </div>
  );
}
